import * as React from 'react';

import { cn } from '@/hen-ui/utils';

export interface NavigationWrapperProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /**
   * The navigation destination URL
   */
  href: string;
  /**
   * Content to wrap with navigation behavior
   */
  children: React.ReactNode;
}

/**
 * NavigationWrapper component following HEN Design System standards.
 *
 * A semantic anchor wrapper that standardizes clickable navigation behavior
 * across HEN UI without requiring individual components to manage linking logic.
 * Renders children without visual modification while providing navigation functionality.
 *
 * @example
 * // Basic usage - wrap any content
 * <NavigationWrapper href="/home">
 *   <Logo />
 * </NavigationWrapper>
 *
 * @example
 * // With custom className
 * <NavigationWrapper href="/products" className="block">
 *   <ProductCard />
 * </NavigationWrapper>
 *
 * @example
 * // External link with security best practices
 * <NavigationWrapper
 *   href="https://example.com"
 *   target="_blank"
 *   rel="noopener noreferrer"
 * >
 *   <ExternalContent />
 * </NavigationWrapper>
 *
 * @example
 * // With aria-label for accessibility
 * <NavigationWrapper href="/" aria-label="Go to homepage">
 *   <Image src="/logo.svg" alt="Company Logo" />
 * </NavigationWrapper>
 */
export const NavigationWrapper = React.forwardRef<
  HTMLAnchorElement,
  NavigationWrapperProps
>(({ className, href, children, ...props }, ref) => {
  return (
    <a
      ref={ref}
      href={href}
      className={cn('inline-block', className)}
      {...props}
    >
      {children}
    </a>
  );
});

NavigationWrapper.displayName = 'NavigationWrapper';
